import { useRef } from "react";
import { Box, DownloadIcon, IconButton, Tooltip } from "@hightouch/htui";
import { downloadCsv } from "src/utils/download";
import { format } from "date-fns";
import { BLOCK_SPACING } from "./constants";

export const MarkdownTable = ({ children }: { children: React.ReactNode }) => {
  const tableRef = useRef<HTMLTableElement>(null);

  const handleExport = () => {
    if (!tableRef.current) return;

    const csvCells = Array.from(tableRef.current.rows, (row) =>
      Array.from(row.cells, (cell) => cell.textContent || ""),
    );

    if (csvCells.length === 0) return;

    const timestamp = format(new Date(), "yyyyMMdd-HHmmss");
    downloadCsv(csvCells, `table-export-${timestamp}.csv`);
  };

  return (
    <Box mb={BLOCK_SPACING}>
      <Box
        display="block"
        width="100%"
        overflow="auto"
        __css={{
          borderWidth: "hairline",
          borderStyle: "solid",
          borderColor: "base.divider",
          borderRadius: "lg",
          table: {
            width: "100%",
            tableLayout: "auto",
            borderSpacing: 0,
            borderCollapse: "collapse",
          },

          "table td, table th, table td * , table th *": {
            fontSize: "md !important",
            lineHeight: "5 !important",
            fontFeatureSettings: "zero 1, tnum 1, lnum 1 !important",
            fontVariantNumeric:
              "lining-nums tabular-nums slashed-zero !important",
            WebkitFontFeatureSettings: "zero 1, tnum 1, lnum 1 !important",
            MozFontFeatureSettings: "zero 1, tnum 1, lnum 1 !important",
          },

          tr: {
            backgroundColor: "white",
            borderColor: "base.divider",
          },

          "tr:last-child td": {
            borderBottom: "none",
          },

          "td:last-child, th:last-child": {
            borderRight: "none",
          },

          th: {
            py: 3,
            px: 4,
            borderBottomWidth: "hairline",
            borderBottomStyle: "solid",
            borderRightWidth: "hairline",
            borderRightStyle: "solid",
            borderColor: "base.divider",
            lineHeight: "5",
            textAlign: "left",
            bg: "base.lightBackground",
          },

          td: {
            py: 3,
            px: 4,
            borderBottomWidth: "hairline",
            borderBottomStyle: "solid",
            borderRightWidth: "hairline",
            borderRightStyle: "solid",
            borderColor: "base.divider",
            lineHeight: "5",
            textAlign: "left",
          },
        }}
      >
        <table ref={tableRef}>{children}</table>
      </Box>
      <Box display="flex" justifyContent="flex-start" mt={2}>
        <Tooltip message="Download as CSV" placement="bottom">
          <IconButton
            aria-label="Download as CSV"
            icon={DownloadIcon}
            size="sm"
            variant="tertiary"
            onClick={handleExport}
          />
        </Tooltip>
      </Box>
    </Box>
  );
};
